'use client'

import type { CSSProperties } from 'react'
import { ContentView, resolveContentDocument, type ContentFrameSpec } from '@/app/richEdit/content'
import { selectAccessState, selectMainState } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { LockedScreen } from '../Blocking/LockedScreen'

const emptyStyle = {
  alignItems: 'center',
  background: 'var(--content-empty-bg, transparent)',
  color: 'var(--content-empty-color, #8a9199)',
  display: 'flex',
  fontSize: 'var(--content-empty-size, 13px)',
  justifyContent: 'center',
  minHeight: 'var(--content-empty-min-height, 52px)',
  padding: 'var(--content-empty-padding, 12px)',
  textAlign: 'center',
} satisfies CSSProperties

/** Shared presentation for an explicitly empty content region. It is a body
 * leaf, not a page/screen: the desk header, menus, actions, and subs remain. */
export function EmptyContent({
  className,
  label = 'No content.',
}: {
  className?: string
  label?: string
}) {
  return (
    <div className={className} style={emptyStyle} data-content-empty="true">
      {label}
    </div>
  )
}

/** One main-body state machine shared by every desk surface.
 *
 * Locked and explicitly empty are distinct semantic states. A missing body
 * that was not declared empty remains a strict data-contract error.
 */
export function MainContent({
  className,
  frame,
  emptyClassName,
  emptyLabel,
}: {
  className?: string
  frame?: ContentFrameSpec
  emptyClassName?: string
  emptyLabel?: string
}) {
  const { state } = useSubsStore()
  const access = selectAccessState(state)
  if (!access.showUnlocked) return <LockedScreen className={className} frame={frame} />

  const main = selectMainState(state)
  const doc = resolveContentDocument(main)
  if (doc) return <ContentView doc={doc} className={className} frame={frame} />
  if (main.isEmpty?.body === true) {
    return <EmptyContent className={emptyClassName} label={emptyLabel} />
  }

  throw new Error('[v2 strict] Missing main message body data without explicit empty state.')
}
